'use client';

import {
  Button,
  IconSearch,
  IconArrowBackward,
  IconClose
} from '@sympla/sympla-components';

import EmptySearchResults from 'components/EmptySearchResults/EmptySearchResults';
import { useInstantSearch } from 'react-instantsearch';
import * as styles from './SearchSuggestions.css';
import HighlightCollections from 'components/HighlightCollections/HighlightCollections';

import { userTokenAlgolia } from 'utils/algolia';
import { ALGOLIA_INDEX_NAME_QUERY_SUGGESTIONS } from 'constants/algolia';
import { isMobile as getIsMobile } from 'utils/misc';

import AlgoliaPopularSearch from 'components/AlgoliaSearch/components/AlgoliaPopularSearch';
import RecentSearches from 'components/RecentSearches/RecentSearches';
import AlgoliaPopularEvents from 'components/AlgoliaSearch/components/AlgoliaPopularEvents';
import TrendingEventsFetcher from 'components/TrendingEvents/TrendingEvents';

type SearchSuggestionsProps = {
  onClickInside?: (e: React.MouseEvent) => void;
  handleSearch: (event: React.FormEvent<HTMLFormElement>) => void;
  inputValue?: string;
  handleTextChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  formRef?: React.RefObject<HTMLFormElement>;
  isNavbarCollapsed: boolean;
  onClose: () => void;
  handleClickOutside: () => void;
  setInputValue: (value: string) => void;
  hasMossColor: boolean;
};

function SearchSuggestions({
  onClickInside,
  onClose,
  handleSearch,
  inputValue,
  handleTextChange,
  inputRef,
  formRef,
  isNavbarCollapsed,
  handleClickOutside,
  setInputValue,
  hasMossColor
}: SearchSuggestionsProps) {
  const { results, scopedResults } = useInstantSearch();
  const isLoading = results === null;
  const isMobile = getIsMobile();

  const defaultEventHitsLimit = 6;
  const mobileHitsLimit = 5;

  const algoliaEventsHitsLimit = isMobile
    ? mobileHitsLimit
    : defaultEventHitsLimit;

  const hasEventResults = !isLoading && results?.nbHits > 0;

  const suggestionIndexResults = scopedResults.find(
    (item) => item.indexId === ALGOLIA_INDEX_NAME_QUERY_SUGGESTIONS
  );
  const hasSuggestionHits = !!(
    suggestionIndexResults &&
    suggestionIndexResults.results &&
    suggestionIndexResults.results.nbHits > 0
  );

  const hasNoContentAtAll =
    !!inputValue && !hasEventResults && !hasSuggestionHits;

  const isSearchActive = !!inputValue && !hasNoContentAtAll;

  const currentTrendingHitsLimit = hasNoContentAtAll
    ? mobileHitsLimit
    : defaultEventHitsLimit;

  const SearchResultsFixed = (
    <div className={styles.searchResults} key="fixed-suggestions">
      <HighlightCollections />
      <AlgoliaPopularSearch
        userTokenAlgolia={userTokenAlgolia}
        hasMossColor={hasMossColor}
      />
      <RecentSearches hasMossColor={hasMossColor} />
    </div>
  );

  const AlgoliaResultsBlock = (
    <div
      className={`${styles.trendingEvents} algolia-event-list-wrapper`}
      key="algolia-results"
    >
      <AlgoliaPopularEvents
        query={inputValue || ''}
        isMobile={isMobile}
        userTokenAlgolia={userTokenAlgolia}
        hitsLimit={algoliaEventsHitsLimit}
        isNavbarCollapsed={isNavbarCollapsed}
        hasMossColor={hasMossColor}
        onClose={onClose}
      />
    </div>
  );

  const TrendingFetcherBlock = (limit: number, key: string) => (
    <div className={styles.trendingEvents} key={key}>
      <TrendingEventsFetcher
        hitsLimit={limit}
        isNavbarCollapsed={isNavbarCollapsed}
        hasMossColor={hasMossColor}
        onClose={onClose}
      />
    </div>
  );

  let contentBlocks: React.ReactNode[] = [];

  if (hasNoContentAtAll) {
    contentBlocks = [
      SearchResultsFixed,
      TrendingFetcherBlock(currentTrendingHitsLimit, 'trending-no-content')
    ];
  } else if (isSearchActive) {
    const activeTrendingBlock = TrendingFetcherBlock(
      defaultEventHitsLimit,
      'trending-active-search'
    );

    if (isMobile) {
      contentBlocks = [
        AlgoliaResultsBlock,
        SearchResultsFixed,
        activeTrendingBlock
      ];
    } else {
      contentBlocks = [SearchResultsFixed, AlgoliaResultsBlock];
    }
  } else {
    contentBlocks = [
      SearchResultsFixed,
      TrendingFetcherBlock(defaultEventHitsLimit, 'trending-initial')
    ];
  }

  const inputSearchStyle = `${styles.searchForm} ${
    hasMossColor ? styles.searchFormMoss : ''
  }`;

  return (
    <>
      <div className={styles.dropdownWrapper} onClick={onClickInside}>
        <div className={styles.searchAndResultsContainer}>
          {(!isNavbarCollapsed || isMobile) && (
            <div className={styles.searchContainer}>
              <div className={styles.searchBox}>
                <div
                  className={`${styles.backButton} ${
                    hasMossColor && styles.backButtonMoss
                  }`}
                >
                  <IconArrowBackward width={16} height={16} />
                  <Button
                    variant="text"
                    upperCase={false}
                    onClick={onClose}
                    color={hasMossColor ? 'mossPale' : 'brand'}
                  >
                    Voltar
                  </Button>
                </div>
                <form
                  ref={formRef}
                  onSubmit={handleSearch}
                  className={inputSearchStyle}
                  role="presentation"
                  data-testid="search-algolia-form"
                >
                  <IconSearch width={24} height={24} />
                  <input
                    ref={inputRef}
                    className={styles.searchInput}
                    placeholder="Buscar experiências"
                    type="text"
                    autoFocus
                    onChange={handleTextChange}
                    value={inputValue}
                    data-testid="search-algolia-input"
                  />
                  <Button
                    variant="text"
                    type="submit"
                    className={
                      inputValue ? styles.searchButton : styles.hiddenButton
                    }
                    data-testid="search-algolia-submit-button"
                  >
                    Buscar
                  </Button>
                  {inputValue && (
                    <IconClose
                      width={20}
                      height={20}
                      className={styles.cleanInput}
                      onClick={() => setInputValue('')}
                      fill="#717680"
                    />
                  )}
                </form>
              </div>
              <Button
                onClick={onClose}
                upperCase={false}
                size="small"
                variant="text"
                className={styles.closeButton}
                aria-label="Fechar"
              >
                <span>Fechar</span>
                <IconClose
                  width={16}
                  height={16}
                  onClick={onClose}
                  fill="#0097FF"
                />
              </Button>
            </div>
          )}
          <div className={styles.searchContent}>
            {hasNoContentAtAll && (
              <div className={styles.emptyResultsContainer}>
                <EmptySearchResults
                  title="Nada por aqui ainda"
                  subtitle="Mas que tal conferir outros eventos que você pode gostar?"
                />
              </div>
            )}
            <div className={`${styles.resultsAndTrendingContainer}`}>
              {contentBlocks}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.bottomOverlay} onClick={handleClickOutside} />
    </>
  );
}

export default SearchSuggestions;
